// i18n
import {
  changeLanguage,
  supportLocales,
  translateInComponent,
} from 'infras/i18n';
import translations from 'presentations/views/components/organisms/EventTypeContactForm/i18n.json';

import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';

import {
  apiPath,
  eventPath,
  eventTypeCompletePath,
  eventTypePath,
  eventTypeWidgetCompletePath,
} from 'presentations/routes/path';
import { get, patch, post } from 'infras/rest_client';
import {
  SharedEventDetailItem,
  EventRescheduleBody,
  EventReserveAnswerBody,
  EventReserveBody,
  InquieryCreateBody,
} from 'presentations/api/schema/event';
import Event from 'domains/models/event';
import { LocationType } from 'domains/models/event_type/locationType';
import Email from 'domains/models/user/email';
import { ErrorList } from 'domains/models';
import {
  currentLocale,
  currentTimeZone,
  DateTime,
  formatToMills,
  formatToYMD,
  getDateTime,
} from 'infras/datetime';
import {
  CalendarDateDetail,
  getAvailableDays,
  TimeList,
} from 'presentations/api/helpers/event_type';
import { setEvent, setTiming } from 'presentations/views/helpers/analytics';
import { GaTracker } from 'presentations/views/helpers/analytics/user_tracking/ga';
import {
  AdminCalendarEventItem,
  ViewerCalendarEventListItem,
} from 'presentations/api/schema/calendar_event';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import { useRequest } from 'presentations/views/hooks/request';
import {
  EventTypeRestrictServiceUsageItem,
  EventTypeServiceRestrictUsageBody,
  EventTypeServiceRestrictUsageResponse,
  SharedEventTypeItem,
} from 'presentations/api/schema/event_type';
import { TeamItem } from 'presentations/api/schema/team';
import { EventTypeUserListItem } from 'presentations/api/schema/event_type_user';
import { SharedGuestUserItem, ViewerItem } from 'presentations/api/schema/user';
import {
  CalendarViewType,
  isListViewType,
  isWeeklyViewType,
} from 'domains/models/event_type/calendarViewType';
import {
  ConfirmationType,
  shouldEventTypeRedirect,
} from 'domains/models/event_type/confirmationType';
import Workflow from 'domains/models/workflow';
import { workflowTypeToAnswerType } from 'domains/models/answer/answerType';
import { useCalendarListDialog } from 'presentations/views/components/organisms/features/calendarIntegration/CalendarListDialog/hooks';
import { TeamUserItem } from 'presentations/api/schema/team_user';
import { isMediaMax } from 'presentations/views/styles';
import { WorkflowType } from 'domains/models/workflow/workflowType';
import { EventTypeServiceListItem } from 'presentations/api/schema/event_type_service';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { EventPaymentCreateBody } from 'presentations/api/schema/event_payment';
import { addEventTypeRedirectProps } from 'presentations/views/helpers/event_type';
import { WebhookEvent } from 'domains/models/webhook/webhookEvent';
import { isMobilePhone } from 'infras/validator';
import {
  flexibleStartWeekDay,
  StartWeekDay,
} from 'domains/models/event_type/startWeekDay';
import { PhoneType } from 'domains/models/event_type_location/phoneType';
import {
  AccountConnectMode,
  Steps,
} from 'presentations/views/components/organisms/EventReservationView';
import { eventTypeThemeColors } from 'domains/models/event_type/color';
import { relativeLuminanceW3C } from 'presentations/views/helpers/color';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import { ViewerTimeZoneType } from 'domains/models/event_type/viewerTimeZoneType';
import { RestrictType } from 'domains/models/event_type_restrict/restrictType';
import { MetaPixelTracker } from 'presentations/views/helpers/analytics/user_tracking/meta_pixel';
import { Currency } from 'domains/models/service_price/currency';
import { UserType } from 'domains/models/event_type_user/user_type';
import { CampaignCode } from 'presentations/routes/helper';
import { Role } from 'domains/models/event_type_user/role';
import {
  getPersistentItem,
  infiniteCacheExpiration,
  setPersistentItem,
} from 'presentations/views/helpers/storage';
import { cacheKey } from './useCache';

interface Props {
  viewer: ViewerItem | null;
  eventType: SharedEventTypeItem;
  event: SharedEventDetailItem | null; // nullでなければ Event Edit
  team: TeamItem;
  locationIds: LocationType[];
  host: EventTypeUserListItem;
  isWidget: boolean;
  locale?: string;
  onetimeLinkId?: string;
  name?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
}

const initialListViewDurationDays = 30;
const listViewDurationDays = 90;
const calendarDateList: CalendarDateDetail[] = [];
const calendarAllTimeDateList: CalendarDateDetail[] = [];
let cachedPeriodStart: DateTime | null = null;
let cachedPeriodEnd: DateTime | null = null;
// GA計測用変数
let startTiming: number;
let endTiming: number;

export enum ExceededType {
  RestrictDuration = 1,
  RestrictCount = 2,
  ZoomRatelimit = 3,
}

export function useEventTypePage(props: Props) {
  const router = useRouter();
  const { query } = router;

  const isProvidedContact = !!(props.name && props.email);
  const isTablet = isMediaMax('tablet');
  const t = translateInComponent(translations);
  const [isFetchFailed, setIsFetchFailed] = useState<boolean>(false);
  const [isFetching, setisFetching] = useState<boolean>(false);
  const [isDoubleBooked, setIsDoubleBooked] = useState<boolean>(false);
  const defaultViewType = props.eventType.calendarViewType;
  const [viewType, setViewType] = useState<CalendarViewType>(defaultViewType);
  const [exceededInfo, setExceededInfo] = useState<{
    exceededType: ExceededType;
  } | null>(null);
  const [restrictServiceUsages, setRestrictServiceUsages] = useState<
    EventTypeRestrictServiceUsageItem[] | null
  >(null);
  const [isExceedServiceRestrict, setIsExceedServiceRestrict] = useState<
    boolean
  >(false);
  const [calendarIntegrationError, setCalendarIntegrationError] = useState<
    ErrorList
  >({});
  const [isShowingParticipants, setIsShowingParticipants] = useState<boolean>(
    false,
  );
  const [isShowingInquityForm, setIsShowingInquityForm] = useState<boolean>(
    false,
  );
  const [showingParticipants, setShowingParticipants] = useState<
    SharedGuestUserItem[]
  >([]);

  const isOwnEventType = useMemo(() => {
    if (props.viewer) {
      const userId = props.viewer.uid;
      return !!props.eventType.users.find((user) => userId === user.uid);
    }
    return false;
  }, [props.eventType, props.viewer]);

  const isWeeklyView = isWeeklyViewType(viewType);
  const isListView = isListViewType(viewType);

  const isSpecifiedDateFromQuery = Boolean(query.date);
  const isSpecifiedDatetimeFromQuery =
    isSpecifiedDateFromQuery && Boolean(query.time);

  const [isInitLoaded, setIsInitLoaded] = useState<boolean>(false);
  const [isWeeklyDataInitialized, setIsWeeklyDataInitialized] = useState<
    boolean
  >(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingViewMore, setIsLoadingViewMore] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const [blocks, setBlocks] = useState<AdminCalendarEventItem[]>([]);
  const [viewerBlocks, setViewerBlocks] = useState<
    ViewerCalendarEventListItem[]
  >([]);
  const [errors, setErrors] = useState<ErrorList>({});
  const [email, setEmail] = useState<string | null>(
    props.email ? props.email : props.viewer ? props.viewer.email : null,
  );
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [inputingTag, setInputingTag] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(
    props.name ? props.name : props.viewer ? props.viewer.name : null,
  );
  const [phoneNumber, setPhoneNumber] = useState<string | null>(
    props.phoneNumber || null,
  );
  const [textRemindCountry, setTextRemindCountry] = useState<any | null>(null);
  const [textRemindNumber, setTextRemindNumber] = useState<string | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const defaultTimeZone = useCallback(() => {
    if (typeof query.tz === 'string') {
      return query.tz;
    }

    if (props.eventType.viewerTimeZoneType === ViewerTimeZoneType.Auto) {
      return currentTimeZone();
    }

    return props.eventType.timeZone;
  }, [query.tz, props.eventType.viewerTimeZoneType, props.eventType.timeZone]);
  const [timeZone, setTimeZone] = useState<string>(defaultTimeZone());
  const [defaultDate, setDefaultDate] = useState<DateTime | null>(
    typeof query.date === 'string'
      ? getDateTime(new Date(query.date), timeZone)
      : null,
  );
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(
    typeof query.date === 'string'
      ? getDateTime(new Date(query.date), timeZone)
      : null,
  );
  const [selectedStartDate, setSelectedStartDate] = useState<DateTime | null>(
    typeof query.date === 'string'
      ? getDateTime(new Date(query.date), timeZone)
      : null,
  );
  const [selectedDatetime, setSelectedDatetime] = useState<DateTime | null>(
    typeof query.date === 'string' && typeof query.time === 'string'
      ? getDateTime(new Date(query.date), timeZone)
      : null,
  );
  const [listViewCursorDate, setListViewCursorDate] = useState<DateTime | null>(
    null,
  );
  const [selectedTimeList, setSelectedTimeList] = useState<TimeList>([]);
  const [selectedTimeLists, setSelectedTimeLists] = useState<TimeList[]>([]);
  const [allTimeLists, setAllTimeLists] = useState<TimeList[]>(
    Array.from({
      length: 7,
    }).map(() => []),
  );
  const [isOpenReservation, setIsOpenReservation] = useState<boolean>(false);

  const [availableDays, setAvailableDays] = useState<boolean[]>([]);

  const isShowLocation = props.locationIds.includes(LocationType.InPerson);
  let initialStep = Steps.Date;
  if (isWeeklyView) {
    initialStep = Steps.DateWeekly;
  }

  // 多言語対応のため、useStateのsetterは直接使わない
  // TODO: ステップが変わるごとデフォルト言語になってしまう問題が解消したら、changeLanguageを行わないように戻す
  const activeStepRef = useRef<Steps>(initialStep);
  const [activeStep, setActiveStepState] = useState<Steps>(initialStep);
  const setActiveStep = (newStep: number) => {
    setActiveStepState(newStep);
    activeStepRef.current = newStep;
    changeLanguage(router.locale || props.locale || currentLocale());
  };

  let accountConnectMode: AccountConnectMode | null = null;
  if (activeStep === Steps.Date || activeStep === Steps.DateWeekly) {
    accountConnectMode = props.viewer
      ? AccountConnectMode.connected
      : AccountConnectMode.connect;
  } else if (activeStep === Steps.Time) {
    // 月ビューの時の時間画面の時にカレンダー未接続と出す意図が不明なので、
    // ロジックは活きとしておくが表示上notConnectedは使わないようにする
    accountConnectMode = props.viewer
      ? AccountConnectMode.connected
      : AccountConnectMode.notConnected;
  }

  const defaultIsShowViewerCalendar =
    !props.isWidget && accountConnectMode === AccountConnectMode.connected;
  const [isShowViewerCalendar, setIsShowViewerCalendar] = useState<boolean>(
    defaultIsShowViewerCalendar,
  );

  useEffect(() => {
    setIsShowViewerCalendar(
      defaultIsShowViewerCalendar && getPersistentItem(cacheKey) !== false,
    );
  }, [defaultIsShowViewerCalendar]);

  const [
    selectedLocationId,
    setSelectedLocationId,
  ] = useState<LocationType | null>(null);
  const [
    selectedPaymentMethod,
    setSelectedPaymentMethod,
  ] = useState<PaymentMethod | null>(null);
  const [isPaidServiceSelected, setIsPaidServiceSelected] = useState<boolean>(
    false,
  );

  // サービス
  const [selectedServices, setSelectedServices] = useState<
    EventTypeServiceListItem[]
  >([]);

  const optionalServices = useMemo(
    () =>
      props.eventType.services
        .filter((service) => !service.isRequired)
        .sort((a, b) => a.sort - b.sort),
    [props.eventType.services],
  );

  const campaignCode = useMemo(() => {
    const code: CampaignCode = {
      term: router.query.utm_term ? String(router.query.utm_term) : undefined,
      content: router.query.utm_content
        ? String(router.query.utm_content)
        : undefined,
      source: router.query.utm_source
        ? String(router.query.utm_source)
        : undefined,
      campaign: router.query.utm_campaign
        ? String(router.query.utm_campaign)
        : undefined,
      medium: router.query.utm_medium
        ? String(router.query.utm_medium)
        : undefined,
    };
    return code;
  }, [router.query]);

  const isSelectedServicesHidePriceOnly = useMemo(
    () => selectedServices.every((s) => !s.isPaid && !s.isShowPrice),
    [selectedServices],
  );

  // カスタム質問
  const customQuestions: Workflow[] = props.eventType.form?.workflows || [];
  const [customQuestionAnswers, setCustomQuestionAnswers] = useState<any[]>(
    new Array(customQuestions.length),
  );
  const [customOtherAnswers, setCustomOtherAnswers] = useState<string[]>(
    new Array(customQuestions.length),
  );

  const eventReservationRef = useRef<HTMLDivElement>(null);

  const timeZonesRequest = useRequest(apiPath('/v1/time_zones'), {
    revalidateOnFocus: false,
  });
  const timeZones: TimeZoneListItem[] = timeZonesRequest.data || [];

  // カレンダーアカウント設定
  const [isShowingCalendarList, setIsShowingCalendarList] = useState<boolean>(
    false,
  );
  const calendarListDialog = useCalendarListDialog({
    isOpen: isShowingCalendarList,
  });

  // ホストの予約可能時間の確認
  const [isHostDiagnosticMode, setIsHostDiagnosticMode] = useState<boolean>(
    false,
  );
  const [blockTimeReasons, setBlockTimeReasons] = useState<any[]>([]);

  // カレンダーで表示する色の取得(カレンダービュー以外取得しない)
  const teamUserRequest = useRequest(
    props.viewer &&
      [
        CalendarViewType.CalendarWeekLegacy,
        CalendarViewType.WeekCalendar,
      ].includes(viewType)
      ? apiPath(`/v1/teams/${props.viewer.team!.uid}/users/${props.viewer.uid}`)
      : null,
    {
      revalidateOnFocus: false,
    },
  );
  const viewerTeamUser: TeamUserItem = teamUserRequest.data || null;

  const resizeWidgetFrame = useCallback(() => {
    parent.postMessage(
      {
        name: 'windowHeight',
        value: document.documentElement.offsetHeight,
      },
      '*',
    );
  }, []);

  const startWeekDay = useMemo((): StartWeekDay => {
    let start = props.eventType.startWeekDay || (0 as StartWeekDay);
    if (start === flexibleStartWeekDay) {
      start = getDateTime().weekday as StartWeekDay;
      if (Number(start) === 7) {
        start = 0;
      }
    }
    return start;
  }, []);

  const displayWeekdays = useMemo(() => {
    return props.eventType.displayWeekDay.split(',').map((w) => Number(w));
  }, [props.eventType.displayWeekDay]);

  const isInputedRequiredContent =
    name &&
    email &&
    ((!props.eventType.setting.isCompanyHidden &&
      props.eventType.setting.isCompanyRequired &&
      company) ||
      props.eventType.setting.isCompanyHidden ||
      !props.eventType.setting.isCompanyRequired) &&
    (props.eventType.setting.isCcEmailsHidden ||
      !props.eventType.setting.isCcEmailsRequired) &&
    (props.eventType.setting.isMessageHidden ||
      !props.eventType.setting.isMessageRequired) &&
    !customQuestions.some((q) => q.isRequired) &&
    (!props.eventType.setting.isTextRemind ||
      !props.eventType.setting.isTextRemindNumberRequired);

  useEffect(() => {
    startTiming = Number(new Date());
  }, []);

  useEffect(() => {
    if (isSelectedServicesHidePriceOnly) {
      setSelectedPaymentMethod(PaymentMethod.None);
    }
  }, [selectedServices, isSelectedServicesHidePriceOnly]);

  useEffect(() => {
    if (email && isProvidedContact && !restrictServiceUsages) {
      fetchServiceRestrictUsage(email).then((result) => {
        if (!result?.isSuccess) {
          return;
        }

        const usage: EventTypeServiceRestrictUsageResponse = result.data;
        if (usage) {
          setRestrictServiceUsages(usage.usages);
        }
      });
    }
  }, [isProvidedContact]);

  const fetchServiceRestrictUsage = async (
    providedEmail: string,
    date?: DateTime,
  ) => {
    const data: EventTypeServiceRestrictUsageBody = {
      email: providedEmail,
    };
    if (date) {
      data.date = date.toISODate();
    }
    const result = await post(
      apiPath(`/v1/event_types/${props.eventType.uid}/service_restrict_usage`),
      {
        data,
      },
    );
    if (!result.isSuccess || !result.data) {
      return;
    }
    return result;
  };

  const retryMax = isWeeklyView ? 2 : 1;
  const initLoad = async (
    periodStart: DateTime,
    periodEnd: DateTime,
    requestTimeZone: string,
    requestOptions: { noCache: boolean } = { noCache: false },
  ) => {
    setIsLoading(true);
    for (let retry = 0; retry <= retryMax; retry++) {
      let startDateTime = periodStart;
      let endDateTime = periodEnd;
      if (isWeeklyView || isListView) {
        const plus = { day: 7 * retry };
        startDateTime = periodStart.plus(plus);
        endDateTime = periodEnd.plus(plus);
      } else {
        const plus = { month: retry };
        startDateTime = startDateTime.plus(plus).startOf('month');
        endDateTime = startDateTime.endOf('month');
      }
      const data = await fetch(
        startDateTime,
        endDateTime,
        requestTimeZone,
        requestOptions,
      );
      if (data.status === 403) {
        setCalendarIntegrationError(data.errors);
        break;
      }
      if (!data || !data.calendar) {
        return;
      }
      const isExist = data && data.calendar.calendarDateList.length > 0;
      if (!isExist && retry < retryMax) continue;

      setViewerBlocks(data!.calendar.viewerBlocks);
      if (!isWeeklyView) {
        const currentDate = startDateTime.startOf('month').startOf('day');
        const {
          days,
          firstAvailableDate,
          firstAvailableTimeList,
        } = getAvailableDays(
          calendarDateList,
          currentDate,
          endDateTime!,
          isHostDiagnosticMode,
        );
        if (!isSpecifiedDateFromQuery && firstAvailableDate) {
          setDefaultDate(firstAvailableDate);
          setSelectedDate(firstAvailableDate);
        }
        if (firstAvailableDate) {
          setListViewCursorDate(
            endDateTime.set({ hour: 0, minute: 0, second: 0 }),
          );
        }
        if (isSpecifiedDateFromQuery) {
          const calendarDate:
            | CalendarDateDetail
            | undefined = calendarDateList.find(
            (calendarDate) => calendarDate.date === formatToYMD(periodStart),
          );
          setSelectedTimeList(calendarDate ? calendarDate.timeList : []);
        } else {
          setSelectedTimeList(firstAvailableTimeList);
        }
        setAvailableDays(days);
      } else {
        setSelectedStartDate(startDateTime);
        toWeekView(
          startDateTime,
          data!.calendar.calendarDateList,
          data!.calendar.calendarAllTimeDateList,
          requestTimeZone,
        );
      }
      break;
    }
    setIsInitLoaded(true);
    setIsLoading(false);
  };

  const { isHost, isAdmin } = useMemo(() => {
    const v = props.viewer;
    const anonymous = { isHost: false, isAdmin: false };
    if (!v) return anonymous;
    const eventTypeUser = props.eventType.users.find(
      (u) =>
        props.eventType.teamId === v.team?.id &&
        u.uid === v.uid &&
        u.userType === UserType.Host,
    );
    if (eventTypeUser) {
      return {
        isHost: true,
        isAdmin: eventTypeUser.role === Role.Admin,
      };
    }
    return anonymous;
  }, [props.viewer, props.eventType]);

  useEffect(() => {
    let periodStart = defaultDate
      ? defaultDate
      : getDateTime().setZone(timeZone);
    let periodEnd: DateTime | undefined;
    if (isWeeklyView) {
      let minusDays = periodStart.weekday === 7 ? 0 : periodStart.weekday;
      minusDays = minusDays - startWeekDay;
      if (minusDays < 0) minusDays += 7;
      const weekStart = periodStart.minus({
        days: minusDays,
      });
      periodStart = weekStart.startOf('day');
      periodEnd = weekStart.plus({ days: 6 }).endOf('day');
      setSelectedStartDate(periodStart);
    } else if (isListView) {
      periodEnd = periodStart.plus({ days: initialListViewDurationDays });
    } else {
      periodEnd = periodStart.endOf('month');
    }
    initLoad(periodStart, periodEnd, timeZone);
  }, []);

  useEffect(() => {
    if (props.isWidget) {
      window.addEventListener('resize', () => {
        resizeWidgetFrame();
      });
    }
  }, []);

  useEffect(() => {
    if (props.isWidget && isInitLoaded) {
      resizeWidgetFrame();
    }
  }, [isInitLoaded]);

  useEffect(() => {
    if (!props.eventType.services || props.eventType.services.length === 0) {
      return;
    }
    if (isInitLoaded) return;
    const requiredItems = props.eventType.services.filter(
      (service) => service.isRequired,
    );
    if (props.eventType.setting.isMultiselectService) {
      setSelectedServices(requiredItems);
      setIsDisabled(false);
      setIsPaidServiceSelected(
        requiredItems.some((s) => Number(s.isPaid) === 1),
      );
    } else {
      const firstRequiredItem = props.eventType.services.find(
        (service) => service.isRequired,
      );
      if (firstRequiredItem) {
        setSelectedServices([firstRequiredItem]);
      }
      setIsDisabled(requiredItems.length > 1);
      setIsPaidServiceSelected(
        !!(firstRequiredItem && firstRequiredItem.isPaid),
      );
    }
  }, [isInitLoaded]);

  useEffect(() => {
    if (props.isWidget) {
      resizeWidgetFrame();
    }
  }, [activeStep]);

  useEffect(() => {
    setIsOpenReservation(true);
  }, [activeStep]);

  useEffect(() => {
    if (isWeeklyView) {
      // 週ビューの初期化
      toWeekView(
        (selectedStartDate || getDateTime()).startOf('day'),
        calendarDateList,
        calendarAllTimeDateList,
      );
    } else if (isListView && !listViewCursorDate) {
      const listViewCursorDate = getDateTime().set({
        hour: 0,
        minute: 0,
        second: 0,
      });
      setListViewCursorDate(listViewCursorDate);
    } else {
      setSelectedStartDate(getDateTime().startOf('month'));
    }

    if (isInitLoaded && !endTiming) {
      endTiming = Number(new Date());
      setTiming({
        name: 'load',
        value: endTiming - startTiming,
        category: 'EventType calendar setup time',
        label: `team_${props.team.id}/event_types_${props.eventType.id}`,
      });
    }
  }, [isInitLoaded]);

  useEffect(() => {
    if (selectedLocationId) {
      validateLocationField();
    }
  }, [selectedLocationId]);

  useEffect(() => {
    if (!isInitLoaded) return;

    reloadCalendar({ activeStep: activeStepRef.current });
    setSelectedDate(null);
  }, [isHostDiagnosticMode]);

  useEffect(() => {
    if (isSpecifiedDatetimeFromQuery) {
      if (typeof query.time === 'string' && selectedDate) {
        // TODO ビューの遷移は発火はしているが、上書きされている
        onTimeSelect(query.time, selectedDate);
      }
    } else if (isSpecifiedDateFromQuery) {
      if (selectedDate && !isWeeklyView) {
        switch (viewType) {
          case CalendarViewType.Month:
          case CalendarViewType.TwoColumnMonthAccent:
          case CalendarViewType.TwoColumnMonth:
            if (isTablet) {
              setActiveStep(Steps.Time);
            }
            break;
          case CalendarViewType.ListTime:
          case CalendarViewType.GridTime:
            break;
          default:
            setActiveStep(Steps.Time);
            break;
        }
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const language = window.navigator.language.substring(0, 2);
      if (supportLocales.includes(language) && language !== router.locale) {
        router.replace(router.asPath, undefined, { locale: language });
      }
    }
  }, []);

  const toWeekView = (
    now: DateTime | null,
    newCalendarDateList: CalendarDateDetail[],
    newCalendarAllTimeDateList: CalendarDateDetail[],
    newTimeZone?: string,
  ) => {
    if (!now) return;

    // 曜日はじまりの設定
    let minusDays = now.weekday === 7 ? 0 : now.weekday;
    minusDays = minusDays - startWeekDay;
    if (minusDays < 0) minusDays += 7;

    const weekStart = now.minus({
      days: minusDays,
    });
    const weekDays = Array.from({
      length: 7,
    }).map((v, i) => {
      const day = weekStart.plus({ days: i });
      return getDateTime().setZone('utc').set({
        year: day.year,
        month: day.month,
        day: day.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
    });
    setSelectedStartDate(weekStart);

    const availableAllTimeLists = weekDays.map((weekDay) => {
      const detail = newCalendarAllTimeDateList.find(
        (d) => d.date === formatToYMD(weekDay),
      );
      if (detail) {
        return detail.timeList;
      } else {
        return [];
      }
    });

    setAllTimeLists(availableAllTimeLists);

    if (isInitLoaded) {
      const timeLists = [];
      for (const weekDay of weekDays) {
        const calendarDate = newCalendarDateList.find(
          (calendarDate) => calendarDate.date === formatToYMD(weekDay),
        );
        if (calendarDate) {
          timeLists.push(calendarDate.timeList);
        } else {
          timeLists.push([]);
        }
      }
      setSelectedTimeLists(timeLists);
      setIsWeeklyDataInitialized(true);
    }
  };

  const reloadScreen = () => {
    setIsDoubleBooked(false);
    setExceededInfo(null);
    setIsExceedServiceRestrict(false);
    setIsLoading(false);
    setIsConfirmLoading(false);
    setSelectedDate(null);
    setSelectedDatetime(null);
    setActiveStep(initialStep);
    reloadCalendar({});
  };

  const fetch = async (
    originalPeriodStart: DateTime,
    originalPeriodEnd: DateTime,
    requestTimeZone: string,
    requestOptions: { noCache: boolean } = {
      noCache: false,
    },
  ) => {
    const periodEnd = isListView
      ? originalPeriodEnd.endOf('day')
      : originalPeriodEnd;
    try {
      const editingEventUid = props.event ? props.event.uid : null;
      if (
        !requestOptions.noCache &&
        cachedPeriodStart &&
        cachedPeriodStart <= originalPeriodStart &&
        cachedPeriodEnd &&
        cachedPeriodEnd >= periodEnd
      ) {
        return {
          errors: null,
          calendar: null,
          status: 304,
        };
      }

      setisFetching(true);
      const result = await get(
        apiPath(`/v1/event_types/${props.eventType.uid}/calendar`, {
          event: editingEventUid,
          periodStart: originalPeriodStart.toMillis(),
          periodEnd: periodEnd.toMillis(),
          timeZone: requestTimeZone,
          diagnostics: isHostDiagnosticMode ? 1 : undefined,
          viewType,
        }),
      );
      setisFetching(false);
      if (!result.isSuccess) {
        return {
          calendar: null,
          status: result.status,
          errors: result.errors,
        };
      }

      const data: {
        calendar: {
          calendarDateList: CalendarDateDetail[];
          calendarAllTimeDateList: CalendarDateDetail[];
          viewerBlocks: ViewerCalendarEventListItem[];
        };
        status: number;
        errors: any;
      } = {
        calendar: result.data,
        status: 200,
        errors: null,
      };

      calendarAllTimeDateList.push(...data.calendar.calendarAllTimeDateList);
      calendarDateList.push(...data.calendar.calendarDateList);
      if (!cachedPeriodStart || cachedPeriodStart > originalPeriodStart) {
        cachedPeriodStart = originalPeriodStart;
      }
      if (!cachedPeriodEnd || cachedPeriodEnd < periodEnd) {
        cachedPeriodEnd = periodEnd;
      }
      return data;
    } catch (error) {
      setIsFetchFailed(true);
      console.debug(error);
      return {
        status: 500,
        errors: error,
      };
    }
  };

  const onTimeZoneSelect = (e: SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    if (name === 'timeZone') {
      updateTimeZone(value);
    }
  };

  const updateTimeZone = (requestTimeZone: string) => {
    resetCachedCalendar();
    setTimeZone(requestTimeZone);

    if (isWeeklyView) {
      const localTime = getDateTime().setZone(requestTimeZone);
      let minusDays = localTime.weekday === 7 ? 0 : localTime.weekday;
      minusDays = minusDays - startWeekDay;
      if (minusDays < 0) minusDays += 7;
      const weekStart = localTime.minus({
        days: minusDays,
      });
      const periodStart = weekStart.startOf('day');
      const periodEnd = weekStart.plus({ days: 6 }).endOf('day');
      initLoad(periodStart, periodEnd, requestTimeZone, { noCache: true });
    } else if (isListView) {
      const periodStart = getDateTime(null, requestTimeZone);
      const periodEnd = periodStart.plus({
        days: initialListViewDurationDays,
      });
      initLoad(periodStart, periodEnd, requestTimeZone, { noCache: true });
    } else {
      const periodStart = selectedStartDate!
        .setZone(requestTimeZone)
        .set({
          year: selectedStartDate!.year,
          month: selectedStartDate!.month,
        })
        .startOf('month');
      const periodEnd = periodStart.endOf('month');
      initLoad(periodStart, periodEnd, requestTimeZone, { noCache: true });
    }

    replaceUrlPath({ selectedDate, timeZone: requestTimeZone });

    setEvent('event type time zone select', {});
  };

  const onDateSelect = (selected: DateTime | null) => {
    // 今月に空いている日がない場合、1日のカレンダーが表示されてしまうのでreturnする
    if (
      !selected ||
      (!isHostDiagnosticMode && !availableDays[selected.day - 1])
    ) {
      return;
    }

    const datetime = getDateTime().setZone('utc').set({
      year: selected.year,
      month: selected.month,
      day: selected.day,
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    });
    setSelectedDate(datetime);
    const calendarDate: CalendarDateDetail | undefined = calendarDateList.find(
      (calendarDate) => calendarDate.date === formatToYMD(datetime),
    );
    setSelectedTimeList(calendarDate ? calendarDate.timeList : []);
    switch (viewType) {
      case CalendarViewType.TwoColumnMonthAccent:
      case CalendarViewType.TwoColumnMonth:
        if (isTablet) {
          setActiveStep(Steps.Time);
        }
        break;
      default:
        setActiveStep(Steps.Time);
        break;
    }

    replaceUrlPath({ selectedDate: selected, timeZone });

    setEvent('event type date select', {}, { isAmplitude: false });
    GaTracker.inviteeSelectDay();
    MetaPixelTracker.inviteeSelectDay();
  };

  const resetCachedCalendar = () => {
    calendarDateList.length = 0;
    calendarAllTimeDateList.length = 0;
    cachedPeriodStart = null;
    cachedPeriodEnd = null;
    setAvailableDays([]);
    setSelectedTimeLists([]);
    setSelectedTimeList([]);
  };

  const reloadCalendar = async (attr: { activeStep?: Steps }) => {
    resetCachedCalendar();
    setIsLoading(true);
    if (isWeeklyView) {
      const periodEnd = selectedStartDate!.plus({ day: 7 }).endOf('day');
      const data = await fetch(selectedStartDate!, periodEnd, timeZone, {
        noCache: true,
      });
      if (!data || !data.calendar) {
        return;
      }
      toWeekView(
        selectedStartDate,
        data!.calendar.calendarDateList,
        data!.calendar.calendarAllTimeDateList,
      );
      setActiveStep(Steps.DateWeekly);
    } else if (isListView) {
      setActiveStep(Steps.Date);
      const start = getDateTime(null, timeZone);
      const end = start.plus({ days: initialListViewDurationDays });
      const data = await fetch(start, end, timeZone, {
        noCache: true,
      });
      if (!data || !data.calendar) {
        return;
      }
      const currentDate = start;
      const newCalendarDateList = data!.calendar.calendarDateList;
      const {
        days,
        firstAvailableDate,
        firstAvailableTimeList,
      } = getAvailableDays(
        newCalendarDateList,
        currentDate,
        end,
        isHostDiagnosticMode,
      );
      if (firstAvailableDate) {
        setDefaultDate(firstAvailableDate);
        setListViewCursorDate(end.set({ hour: 0, minute: 0, second: 0 }));
      } else {
        setListViewCursorDate(null);
      }
      setSelectedTimeList(firstAvailableTimeList);
      setAvailableDays(days);
    } else {
      setActiveStep(attr.activeStep ?? Steps.Date);
      const start = selectedStartDate!
        .set({ month: selectedStartDate!.month })
        .startOf('month');
      const end = start.endOf('month');
      const data = await fetch(start, end, timeZone, {
        noCache: true,
      });
      if (!data || !data.calendar) {
        return;
      }
      const currentDate = start;
      const newCalendarDateList = data!.calendar.calendarDateList;
      const {
        days,
        firstAvailableDate,
        firstAvailableTimeList,
      } = getAvailableDays(
        newCalendarDateList,
        currentDate,
        end,
        isHostDiagnosticMode,
      );
      if (firstAvailableDate) {
        setDefaultDate(firstAvailableDate);
        setSelectedDate(firstAvailableDate);
      } else {
        setSelectedDate(null);
      }
      setSelectedTimeList(firstAvailableTimeList);
      setAvailableDays(days);
    }
    setIsLoading(false);
  };

  const forwardToLocationOrSkip = () => {
    let nextStep = Steps.Contact;
    if (optionalServices.length > 0) {
      nextStep = Steps.Service;
    }

    let forwardStep = Steps.Location;
    if (props.locationIds.length === 0) {
      forwardStep = nextStep;
    } else if (props.locationIds.length === 1) {
      forwardStep = nextStep;
      setSelectedLocationId(props.locationIds[0]);
    }
    setActiveStep(forwardStep);
  };

  const onTimeSelect = async (
    time: string,
    date?: DateTime,
    participants?: SharedGuestUserItem[],
  ) => {
    let selectDate = selectedDate;

    if (selectedDate && email && isProvidedContact) {
      const restrictRangeResult = await fetchServiceRestrictUsage(
        email,
        selectedDate,
      );
      if (!restrictRangeResult?.isSuccess) {
        return;
      }

      const data: EventTypeServiceRestrictUsageResponse =
        restrictRangeResult.data;
      setRestrictServiceUsages(data.usages);
      for (const usage of data.usages) {
        if (
          usage.isEnableUserMaxMonthlyUsesCount &&
          usage.userMaxMonthlyUsesCount &&
          usage.userCurrentMonthlyUsesCount >= usage.userMaxMonthlyUsesCount
        ) {
          setIsExceedServiceRestrict(true);
          return;
        }
        if (
          usage.isEnableUserMaxWeeklyUsesCount &&
          usage.userMaxWeeklyUsesCount &&
          usage.userCurrentWeeklyUsesCount >= usage.userMaxWeeklyUsesCount
        ) {
          setIsExceedServiceRestrict(true);
          return;
        }
        if (
          usage.isEnableUserMaxDailyUsesCount &&
          usage.userMaxDailyUsesCount &&
          usage.userCurrentDailyUsesCount >= usage.userMaxDailyUsesCount
        ) {
          setIsExceedServiceRestrict(true);
          return;
        }
      }
    }

    // dateが来たらselectedDateを更新
    if (date) {
      selectDate = getDateTime().setZone('utc').set({
        year: date.year,
        month: date.month,
        day: date.day,
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      setSelectedDate(selectDate);
      setEvent('event type date select', {}, { isAmplitude: false });
      GaTracker.inviteeSelectDay();
      MetaPixelTracker.inviteeSelectDay();
    }
    if (!selectDate) return;

    const [hour, minute] = time.split(':');
    if (!hour) return;
    if (!minute) return;

    // ここで指定のtimezoneをセット
    const startedAt = selectDate.setZone(timeZone).set({
      year: selectDate.year,
      month: selectDate.month,
      day: selectDate.day,
      hour: Number(hour),
      minute: Number(minute),
      second: 0,
    });
    setSelectedDatetime(startedAt);
    // 必須項目が全て入っていそうならvalidateAllして、送信ボタンをアクティブに
    // それ以外は送信ボタンは非アクティブスタート
    if (isInputedRequiredContent) {
      validateAll();
    }

    if (!props.event) {
      // 参加者がいるかつ参加者の公開ならLocationに遷移せず、モーダルを出す
      if (
        props.eventType.isRevealGuestUser &&
        (participants || []).length > 0
      ) {
        setIsShowingParticipants(true);
        setShowingParticipants(participants || []);
      } else {
        // ロケーション有無で遷移先を変更
        forwardToLocationOrSkip();
        scrollToEventReservationView(isWeeklyView);
      }
    }

    replaceUrlPath({ selectedDatetime: startedAt, timeZone });

    setEvent('event type time select', {}, { isAmplitude: false });
    GaTracker.inviteeSelectTime();
    MetaPixelTracker.inviteeSelectTime();

    scrollToTop();
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    if (props.isWidget && isListView) {
      parent.postMessage(
        {
          name: 'scrollWidgetTop',
        },
        '*',
      );
    }
  };

  const validateLocationField = (): boolean => {
    setIsDisabled(true);
    const newErrors: ErrorList = {
      ...validate('phoneNumber', phoneNumber),
    };
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return false;
    }
    setIsDisabled(false);
    return true;
  };

  const validateAll = (): boolean => {
    setIsDisabled(true);
    let newErrors: ErrorList = {
      ...validate('email', email),
      ...validate('name', name),
      ...validate('company', company),
      ...validate('phoneNumber', phoneNumber),
      ...validate('textRemindNumber', {
        countryCode: textRemindCountry?.dialCode,
        number: textRemindNumber,
      }),
      ...validate('ccEmails', ccEmails),
      ...validate('message', message),
    };
    customQuestions.forEach((q, i) => {
      newErrors = {
        ...newErrors,
        ...validate(`customQuestion.${i}`, customQuestionAnswers[i]),
      };

      if (q.isRequired && q.hasOtherChoice) {
        newErrors = {
          ...newErrors,
          ...validateOtherAnswer(
            `customQuestion.${i}`,
            customQuestionAnswers[i],
            customOtherAnswers[i],
          ),
        };
      }
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return false;
    }
    setIsDisabled(false);
    return true;
  };

  const validateInquiryForm = (): boolean => {
    setIsDisabled(true);
    const newErrors: ErrorList = {
      ...validate('email', email),
      ...validate('name', name),
      ...validate('company', company),
      ...validate('message', message),
    };
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return false;
    }
    setIsDisabled(false);
    return true;
  };

  const onBlur = (e: any) => {
    const { name, value } = e.currentTarget;
    const newErrors = validate(name, value);
    setErrors(newErrors);
  };

  const onChangeTextRemindNumber = (value: string, country: any) => {
    if (value.slice(country.dialCode.length)) {
      setIsDisabled(false);
      const internalPhoneNumber = value.slice(country.dialCode.length);
      setTextRemindNumber(internalPhoneNumber);
      setTextRemindCountry(country);
    } else {
      setIsDisabled(true);
      setTextRemindNumber(null);
      setTextRemindCountry(country);
    }

    const newErrors: ErrorList = { ...errors };
    delete newErrors['textRemindNumber'];
    setErrors(newErrors);
  };

  const onChange = (e: any) => {
    setIsDisabled(false);
    const { name, value } = e.currentTarget;
    let updateValue = value;
    switch (name) {
      case 'email':
        setEmail(updateValue);
        break;
      case 'message':
        setMessage(updateValue);
        break;
      case 'name':
        setName(updateValue);
        break;
      case 'company':
        setCompany(updateValue);
        break;
      case 'phoneNumber':
        setPhoneNumber(updateValue);
        break;
      default:
        if (typeof name === 'string' && name.startsWith('customQuestion')) {
          const index = Number(name.split('.')[1]);
          const newAnswers = [...customQuestionAnswers];
          const customQuestion = customQuestions[index];
          if (
            customQuestion.workflowType ===
            WorkflowType.QuestionAnswerCheckboxes
          ) {
            if (!newAnswers[index]) {
              newAnswers[index] = [updateValue];
              updateValue = newAnswers[index];
            } else if (typeof newAnswers[index] === 'object') {
              if (newAnswers[index].includes(updateValue)) {
                newAnswers[index].splice(
                  newAnswers[index].indexOf(updateValue),
                  1,
                );
              } else {
                newAnswers[index].push(updateValue);
              }
              updateValue = newAnswers[index];
            }
          } else {
            newAnswers[index] = updateValue;
          }

          setCustomQuestionAnswers(newAnswers);
        }
        break;
    }
    const newErrors = validate(name, updateValue);
    if (Object.keys(newErrors).length > 0) {
      setIsDisabled(true);
    }
    setErrors(newErrors);
  };

  const onClickOtherAnswer = (e: any) => {
    setIsDisabled(false);
    const { name, value } = e.currentTarget;
    if (typeof name === 'string' && name.startsWith('customQuestion')) {
      const index = Number(name.split('.')[1]);
      const newOtherAnswers = [...customOtherAnswers];
      newOtherAnswers[index] = value;
      setCustomOtherAnswers(newOtherAnswers);

      const newAnswers = [...customQuestionAnswers];
      const customQuestion = customQuestions[index];
      if (value) {
        if (
          customQuestion.workflowType ===
          WorkflowType.QuestionAnswerMultipleChoice
        ) {
          newAnswers[index] = t('other');
          setCustomQuestionAnswers(newAnswers);
        } else if (
          customQuestion.workflowType === WorkflowType.QuestionAnswerCheckboxes
        ) {
          if (!newAnswers[index]) {
            newAnswers[index] = [t('other')];
          } else if (typeof newAnswers[index] === 'object') {
            if (!newAnswers[index].includes(t('other'))) {
              newAnswers[index].push(t('other'));
            }
          }
          setCustomQuestionAnswers(newAnswers);
        }
      }
      const newErrors = validateOtherAnswer(
        `customQuestion.${index}`,
        customQuestionAnswers[index],
        value,
      );
      setErrors(newErrors);
    }
  };

  const validateOtherAnswer = (
    name: string,
    questionAnswer: any,
    otherAnswer: any,
  ): ErrorList => {
    const newErrors: ErrorList = {
      ...errors,
    };
    if (typeof name === 'string' && name.startsWith('customQuestion')) {
      const customErrors: string[] = [];
      const index = Number(name.split('.')[1]);

      if (
        customQuestions[index].isRequired &&
        customQuestions[index].hasOtherChoice
      ) {
        if (
          customQuestions[index].workflowType ===
          WorkflowType.QuestionAnswerCheckboxes
        ) {
          if (
            Array.isArray(questionAnswer) &&
            questionAnswer.some((q) => q === t('other')) &&
            !otherAnswer
          ) {
            customErrors.push('model.EventContact.custom.E001');
          }
        } else {
          if (questionAnswer === t('other') && !otherAnswer) {
            customErrors.push('model.EventContact.custom.E001');
          }
        }

        newErrors[name] = customErrors;
        if (customErrors.length === 0) {
          delete newErrors[name];
        }
      }
    }
    return newErrors;
  };

  const validate = (name: string, value: any): ErrorList => {
    const newErrors: ErrorList = {
      ...errors,
    };
    switch (name) {
      case 'email':
        const emailErrors: string[] = [];
        if (!value) {
          // emailErrors.push('model.EventContact.email.E001');
        }
        const newEmail = new Email(value);
        if (!newEmail.isValid()) {
          emailErrors.push('model.EventContact.email.E002');
        }
        newErrors['email'] = emailErrors;
        if (emailErrors.length === 0) {
          delete newErrors['email'];
        }
        break;
      case 'message':
        const messageErrors: string[] = [];
        if (isShowingInquityForm) {
          if (!value) {
            messageErrors.push('model.EventContact.message.E002');
          }
        } else if (
          props.eventType.setting.isMessageRequired &&
          !props.eventType.setting.isMessageHidden &&
          !value
        ) {
          messageErrors.push('model.EventContact.message.E002');
        }
        newErrors['message'] = messageErrors;
        if (messageErrors.length === 0) {
          delete newErrors['message'];
        }
        break;
      case 'name':
        const nameErrors: string[] = [];
        if (!value) {
          nameErrors.push('model.EventContact.name.E001');
        }
        newErrors['name'] = nameErrors;
        if (nameErrors.length === 0) {
          delete newErrors['name'];
        }
        break;
      case 'company':
        const companyErrors: string[] = [];
        if (
          props.eventType.setting.isCompanyRequired &&
          !props.eventType.setting.isCompanyHidden &&
          !value
        ) {
          companyErrors.push('model.EventContact.company.E002');
        }
        newErrors['company'] = companyErrors;
        if (companyErrors.length === 0) {
          delete newErrors['company'];
        }
        break;
      case 'phoneNumber':
        const phoneNumberErrors: string[] = [];
        if (
          selectedLocationId === LocationType.Phone &&
          props.eventType.location?.phoneType === PhoneType.CallFromHost &&
          !value
        ) {
          phoneNumberErrors.push('model.EventContact.phoneNumber.E002');
        }
        newErrors['phoneNumber'] = phoneNumberErrors;
        if (phoneNumberErrors.length === 0) {
          delete newErrors['phoneNumber'];
        }
        break;
      case 'textRemindNumber':
        if (!props.eventType.setting.isTextRemind) {
          break;
        }
        const textRemindNumberErrors: string[] = [];
        let textRemindFormatNumber;
        if (textRemindCountry && textRemindNumber) {
          textRemindFormatNumber = '+' + textRemindCountry.dialCode;
          if (textRemindNumber[0] === '0') {
            textRemindFormatNumber += textRemindNumber.slice(1);
          } else {
            textRemindFormatNumber += textRemindNumber;
          }
        }
        if (
          props.eventType.setting.isTextRemindNumberRequired &&
          !textRemindFormatNumber
        ) {
          textRemindNumberErrors.push(
            'model.EventContact.textRemindNumber.E002',
          );
        }
        if (textRemindFormatNumber && !isMobilePhone(textRemindFormatNumber)) {
          textRemindNumberErrors.push(
            'model.EventContact.textRemindNumber.E001',
          );
        }

        newErrors['textRemindNumber'] = textRemindNumberErrors;
        if (textRemindNumberErrors.length === 0) {
          delete newErrors['textRemindNumber'];
        }
        break;
      case 'ccEmails':
        const ccEmailsErrors: string[] = [];
        if (!Array.isArray(value)) {
          break;
        }
        if (
          props.eventType.setting.isCcEmailsRequired &&
          !props.eventType.setting.isCcEmailsHidden &&
          value &&
          value.length === 0
        ) {
          ccEmailsErrors.push('model.EventContact.ccEmails.E001');
        }
        const ccEmailList = value.map((e: string) => new Email(e));
        for (const ccEmail of ccEmailList) {
          if (!ccEmail.isValid()) {
            ccEmailsErrors.push('model.EventContact.ccEmails.E002');
            break;
          }
        }
        // ccEmailsに重複しているメールアドレスがあるかどうか
        if (new Set(value).size !== value.length) {
          ccEmailsErrors.push('model.EventContact.ccEmails.E004');
        }
        newErrors['ccEmails'] = ccEmailsErrors;
        if (ccEmailsErrors.length === 0) {
          delete newErrors['ccEmails'];
        }
        break;
      default:
        if (typeof name === 'string' && name.startsWith('customQuestion')) {
          const customErrors: string[] = [];
          const index = Number(name.split('.')[1]);
          if (customQuestions[index].isRequired) {
            if (
              customQuestions[index].workflowType ===
              WorkflowType.QuestionAnswerCheckboxes
            ) {
              if (!Array.isArray(value) || value.length === 0) {
                customErrors.push('model.EventContact.custom.E001');
              }
            } else if (!value) {
              customErrors.push('model.EventContact.custom.E001');
            }
          }
          newErrors[name] = customErrors;
          if (customErrors.length === 0) {
            delete newErrors[name];
          }
        }
        break;
    }
    return newErrors;
  };

  const onUpdate = async () => {
    if (!props.event) return;
    setIsConfirmLoading(true);
    try {
      const data: EventRescheduleBody = {
        startTime: formatToMills(selectedDatetime),
        timeZone: timeZone!,
        locationId: selectedLocationId,
      };
      const result = await patch(
        apiPath(
          `/v1/teams/${props.eventType.teamId}/events/${props.event.uid}/reschedule`,
        ),
        { data },
      );
      if (result.status === 409) {
        if (Array.isArray(result.errors?.eventTypeRestrict)) {
          const exceededType =
            result.errors.eventTypeRestrict[0].restrictType ===
            RestrictType.Count
              ? ExceededType.RestrictCount
              : ExceededType.RestrictDuration;
          setExceededInfo({ exceededType });
        } else if (Array.isArray(result.errors?.accountZoomUsage)) {
          setExceededInfo({ exceededType: ExceededType.ZoomRatelimit });
        } else {
          setIsDoubleBooked(true);
        }
        setIsLoading(false);
        return;
      } else if (result.status === 422) {
        setSnackbarMessage(result.errorMessage ?? null);
        setIsLoading(false);
        setIsConfirmLoading(false);
        return;
      }
      if (result.data) {
        setEvent('event update', {
          locationId: props.event.locationId,
        });

        if (
          props.host.uid !== props.viewer?.uid &&
          shouldEventTypeRedirect(props.eventType.rescheduleConfirmationType) &&
          props.eventType.rescheduleConfirmRedirectUrl
        ) {
          let url = props.eventType.rescheduleConfirmRedirectUrl;
          if (
            props.eventType.rescheduleConfirmationType ===
            ConfirmationType.RedirectWithProps
          ) {
            url = addEventTypeRedirectProps(
              WebhookEvent.GuestRescheduled,
              url,
              props.eventType,
              props.event,
              campaignCode,
            );
          }
          const redirectUrl = url;
          window.location.href = redirectUrl;
        } else {
          router.push(eventPath(props.event.uid));
        }
      }
    } catch (error) {
      console.debug(error);
      setIsConfirmLoading(false);
    }
  };

  const onSubmitContact = async (e: any) => {
    if (!selectedDatetime) return;
    if (!validateAll()) {
      setIsLoading(true);
      // 少しローディングさせてから戻す
      setTimeout(() => {
        setSnackbarMessage(t('Error Required'));
        setIsLoading(false);
      }, 1000);
      return;
    }

    if (selectedServices.length > 0) {
      if (isPaidServiceSelected) {
        setActiveStep(Steps.PaymentMethod);
      } else {
        if (isSelectedServicesHidePriceOnly) {
          onSubmit(e);
        } else {
          setSelectedPaymentMethod(PaymentMethod.None);
          setActiveStep(Steps.PaymentPreview);
        }
      }
      scrollToEventReservationView();
      return;
    }
    onSubmit(e);
  };

  const onSubmit = async (e: any) => {
    const data: EventReserveBody = generateEventReserveBody();

    setIsLoading(true);
    const result = await post(
      apiPath(`/v1/teams/${props.team.id}/events/reserve`),
      { data },
    );
    if (!result.isSuccess) {
      if (result.status === 409) {
        if (Array.isArray(result.errors?.eventTypeRestrict)) {
          const exceededType =
            result.errors.eventTypeRestrict[0].restrictType ===
            RestrictType.Count
              ? ExceededType.RestrictCount
              : ExceededType.RestrictDuration;
          setExceededInfo({ exceededType });
        } else if (Array.isArray(result.errors?.accountZoomUsage)) {
          setExceededInfo({ exceededType: ExceededType.ZoomRatelimit });
        } else {
          setIsDoubleBooked(true);
        }
        setIsLoading(false);
        return;
      }
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }
    const newEvent = new Event(result.data);
    setEvent('event submit', {
      locationId: newEvent.locationId,
      isWidget: props.isWidget,
    });
    GaTracker.inviteeMeetingScheduled();
    MetaPixelTracker.inviteeMeetingScheduled();
    if (
      shouldEventTypeRedirect(props.eventType.confirmationType) &&
      props.eventType.confirmRedirectUrl
    ) {
      let url = props.eventType.confirmRedirectUrl;
      if (
        props.eventType.confirmationType === ConfirmationType.RedirectWithProps
      ) {
        url = addEventTypeRedirectProps(
          WebhookEvent.GuestBooked,
          url,
          props.eventType,
          newEvent,
          campaignCode,
        );
      }
      const redirectUrl = url;

      if (props.isWidget) {
        parent.postMessage(
          {
            name: 'redirectUrl',
            value: redirectUrl,
          },
          '*',
        );
      } else {
        window.location.href = redirectUrl;
      }
    } else {
      if (props.isWidget) {
        router.push(
          eventTypeWidgetCompletePath(props.eventType.uid, newEvent.uid, false),
        );
      } else {
        router.push(
          eventTypeCompletePath(props.eventType.uid, newEvent.uid, false),
        );
      }
    }
    // setIsLoading(false);
  };

  const onSubmitPayment = async (e: any) => {
    if (props.eventType.setting.isBookingRequest) {
      await onSubmit(e);
      return;
    }

    if (!selectedDatetime) return;
    if (!validateAll()) {
      setIsLoading(true);
      // 少しローディングさせてから戻す
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return;
    }

    if (
      selectedPaymentMethod === PaymentMethod.None ||
      selectedPaymentMethod === PaymentMethod.Onsite
    ) {
      onSubmit(e);
      return;
    }

    const body: EventReserveBody = generateEventReserveBody();
    const stripePromise = loadStripe(String(process.env['STRIPE_API_KEY']), {
      stripeAccount: props.eventType.stripeAccountId!,
    });

    const stripeClient = await stripePromise;
    if (stripeClient) {
      setIsLoading(true);
      // 仮予約する
      const tentativeReserveResult = await post(
        apiPath(`/v1/teams/${props.team.id}/events/reserve`),
        {
          data: {
            ...body,
            isWidget: props.isWidget,
            cancelUrl: window.location.href,
          },
        },
      );
      if (!tentativeReserveResult.isSuccess) {
        if (tentativeReserveResult.status === 409) {
          if (Array.isArray(tentativeReserveResult.errors?.eventTypeRestrict)) {
            const exceededType =
              tentativeReserveResult.errors.eventTypeRestrict[0]
                .restrictType === RestrictType.Count
                ? ExceededType.RestrictCount
                : ExceededType.RestrictDuration;
            setExceededInfo({ exceededType });
          } else if (
            Array.isArray(tentativeReserveResult.errors?.accountZoomUsage)
          ) {
            setExceededInfo({ exceededType: ExceededType.ZoomRatelimit });
          } else {
            setIsDoubleBooked(true);
          }
          setIsLoading(false);
          return;
        }
        setErrors(tentativeReserveResult.errors);
        setIsLoading(false);
        return;
      }
      const result = tentativeReserveResult.data || null;
      if (!result) {
        console.log('event not found');
        setIsLoading(false);
        return;
      }

      const { error } = await stripeClient.redirectToCheckout({
        sessionId: result.stripeSessionId,
      });
      setEvent('event redirect to checkout', {
        sessionId: result.stripeSessionId,
      });
    }
    setIsLoading(true);
  };

  const onInputChangeTag = async (e: any, value: string) => {
    setInputingTag(value);
  };

  const onSubmitTag = async (e: any) => {
    if (inputingTag) {
      const newEmails = [...ccEmails, inputingTag];
      setCcEmails(newEmails);
      setInputingTag(null);

      const newErrors = validate('ccEmails', newEmails);
      setIsDisabled(Object.keys(newErrors).length > 0);
      setErrors(newErrors);
    }
  };

  const onDeleteTag = async (deleteIndex: number) => {
    const newEmails = [...ccEmails];
    newEmails.splice(deleteIndex, 1);
    setCcEmails(newEmails);

    const newErrors = validate('ccEmails', newEmails);
    setIsDisabled(Object.keys(newErrors).length > 0);
    setErrors(newErrors);
  };

  const onClickBack = async () => {
    if (activeStep === initialStep) return;

    // 支払い方法選択不要の場合、PaymentPreviewからContactに戻る
    if (
      activeStep === Steps.PaymentPreview &&
      selectedServices.length > 0 &&
      !isPaidServiceSelected
    ) {
      setActiveStep(Steps.Contact);
      return;
    }

    if (activeStep === Steps.Location) {
      setSelectedLocationId(null);
    }

    let dateViewStep = Steps.Time;
    switch (viewType) {
      case CalendarViewType.TwoColumnMonthAccent:
      case CalendarViewType.TwoColumnMonth:
        // 月＋時間系
        if (!isTablet) {
          dateViewStep = Steps.Date;
        }
        break;
      case CalendarViewType.ListTime:
      case CalendarViewType.GridTime:
        dateViewStep = Steps.Date;
        break;
      case CalendarViewType.Week:
      case CalendarViewType.WeekCalendar:
      case CalendarViewType.CalendarWeekLegacy:
        // 週ビュー系
        dateViewStep = Steps.DateWeekly;
        break;
      default:
        break;
    }

    let backToStep = activeStep - 1;

    if (backToStep === Steps.Service) {
      // ・オプションサービスなしならスキップする
      if (optionalServices.length < 1) {
        backToStep = backToStep - 1;
      }
    }
    if (backToStep === Steps.Location) {
      // ・ロケーションなしならスキップする
      if (props.locationIds.length <= 1) {
        backToStep = backToStep - 1;
      } else {
        setSelectedLocationId(null);
      }
    }

    if ([Steps.Time].includes(backToStep)) {
      // ・各ビュータイプへ
      backToStep = dateViewStep;
      // 時刻の選択からバックした場合は時刻選択は消去する
      setSelectedDatetime(null);
      replaceUrlPath({
        selectedDate,
        selectedDatetime: null,
        timeZone,
      });
    }
    setActiveStep(backToStep);
  };

  const onSelectLocationType = async (locationId?: LocationType) => {
    if (optionalServices.length > 0) {
      setActiveStep(Steps.Service);
    } else {
      setActiveStep(Steps.Contact);
    }
    setIsLoading(true);
    const processStartTime = new Date().getTime() / 1000;
    scrollToEventReservationView();

    if (locationId) {
      setSelectedLocationId(locationId);
    }
    setIsLoading(false);
    setTimeout(() => {
      const processEndTime = new Date().getTime() / 1000;
      const processTime =
        Math.floor((processEndTime - processStartTime) * 10) / 10;
      setEvent('event type location select', {
        blocksCount: blocks ? blocks.length : null,
        processTime,
        isWidget: props.isWidget,
      });
    }, 500);
  };

  const onClickTerm = (e: SyntheticEvent, url: string) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(url, undefined, 'width=300');
  };

  const onSelectPaymentMethod = async (paymentMethod: PaymentMethod) => {
    setActiveStep(Steps.PaymentPreview);
    setIsLoading(true);
    setSelectedPaymentMethod(paymentMethod);
    scrollToEventReservationView();

    const processStartTime = new Date().getTime() / 1000;
    setTimeout(() => {
      setIsLoading(false);
      const processEndTime = new Date().getTime() / 1000;
      const processTime =
        Math.floor((processEndTime - processStartTime) * 10) / 10;
      setEvent('event type payment method select', {
        paymentMethod,
        processTime,
        isWidget: props.isWidget,
      });
    }, 500);
  };

  const replaceUrlPath = useCallback(
    ({
      selectedDate,
      selectedDatetime,
      timeZone,
    }: {
      selectedDate?: DateTime | null;
      selectedDatetime?: DateTime | null;
      timeZone: string;
    }) => {
      const teamId = query.teamId;
      const eventTypeId = query.eventTypeId;
      let params = undefined;
      if (typeof teamId === 'string' && typeof eventTypeId === 'string') {
        if (selectedDate && timeZone) {
          params = {
            date: encodeURIComponent(selectedDate.toFormat('yyyy-MM-dd')),
            timeZone: encodeURIComponent(timeZone),
          };
        } else if (selectedDatetime) {
          params = {
            date: encodeURIComponent(selectedDatetime.toISO()),
            time: encodeURIComponent(selectedDatetime.toFormat('HH:mm')),
            timeZone: encodeURIComponent(timeZone),
          };
        } else if (timeZone) {
          params = {
            timeZone: encodeURIComponent(timeZone),
          };
        }

        params = {
          // TODO: 許可されていないURL引数全てを持ち続けるのはセキュリティ観点で好ましくないので改善が必要
          ...router.query,
          ...params,
        };

        const url = eventTypePath(
          {
            teamSlug: teamId,
            eventTypeSlug: eventTypeId,
            onetimeLinkId: props.onetimeLinkId,
          },
          params,
        );

        router.replace(url, undefined, { shallow: true });
      }
    },
    [query, router, props.onetimeLinkId],
  );

  function generateEventReserveBody() {
    // カスタム質問
    const answers: EventReserveAnswerBody[] = [];
    if (props.eventType.form && props.eventType.form.id) {
      customQuestions.forEach((q, index) => {
        const answerType = workflowTypeToAnswerType(q.workflowType);
        if (!answerType) return;
        let answer = customQuestionAnswers[index];
        if (q.hasOtherChoice) {
          if (
            q.workflowType === WorkflowType.QuestionAnswerCheckboxes &&
            Array.isArray(customQuestionAnswers[index]) &&
            customQuestionAnswers[index].includes(t('other'))
          ) {
            const customOtherAnswer = customOtherAnswers[index];
            if (customOtherAnswer) {
              customQuestionAnswers[index].splice(
                customQuestionAnswers[index].indexOf(t('other')),
                1,
              );
              customQuestionAnswers[index].push(
                t('other') + `（${customOtherAnswer}）`,
              );
            }
          } else if (
            (q.workflowType === WorkflowType.QuestionAnswerMultipleChoice ||
              q.workflowType === WorkflowType.QuestionAnswerDropDown) &&
            customQuestionAnswers[index] === t('other')
          ) {
            const customOtherAnswer = customOtherAnswers[index];
            if (customOtherAnswer) {
              answer = t('other') + `（${customOtherAnswer}）`;
            }
          }
        }

        answers.push({
          content: answer,
          answerUid: q.uid,
        });
      });
    }

    let payment: EventPaymentCreateBody | null = null;
    if (selectedPaymentMethod !== null && selectedServices.length > 0) {
      let currency = Currency.JPY;
      for (const service of selectedServices) {
        if (service?.price?.currency) {
          currency = service.price.currency;
          break;
        }
      }
      payment = {
        paymentMethod: selectedPaymentMethod,
        items: selectedServices.map((s) => {
          return {
            serviceId: s.serviceId!,
          };
        }),
        currency,
      };
    }

    const data: EventReserveBody = {
      eventTypeId: props.eventType.id,
      name: name!,
      email: email!,
      startTime: formatToMills(selectedDatetime),
      timeZone: timeZone!,
      locationId: selectedLocationId,
      phoneNumber,
      textRemindCountryCode: textRemindCountry?.dialCode
        ? '+' + textRemindCountry.dialCode
        : null,
      textRemindNumber,
      message,
      ccEmails,
      company,
      answers,
      payment,
      onetimeLinkId: props.onetimeLinkId,
      locale: router.locale,
      tracking: {
        medium: query.utm_medium ? String(query.utm_medium) : null,
        campaign: query.utm_campaign ? String(query.utm_campaign) : null,
        source: query.utm_source ? String(query.utm_source) : null,
        content: query.utm_content ? String(query.utm_content) : null,
        term: query.utm_term ? String(query.utm_term) : null,
      },
    };

    return data;
  }

  const onChangeMonth = async (date: DateTime) => {
    const { year, month, day } = date;
    const selectedDate = date.setZone(timeZone).set({ year, month, day });
    setIsLoading(true);
    let data;
    if (isWeeklyView) {
      const start = selectedDate.startOf('day');
      const end = date.plus({ day: 6 }).endOf('day');
      data = await fetch(start, end, timeZone, { noCache: true });
      toWeekView(start, calendarDateList, calendarAllTimeDateList);
      replaceUrlPath({ selectedDate: start, timeZone });
    } else {
      const start = selectedDate.startOf('month');
      const end = start.endOf('month');
      data = await fetch(start, end, timeZone, { noCache: true });
      const currentDate = start;
      const { days, firstAvailableTimeList } = getAvailableDays(
        calendarDateList,
        currentDate,
        end,
        isHostDiagnosticMode,
      );
      setAvailableDays(days);
    }
    if (data) {
      if (data.status === 403) {
        setCalendarIntegrationError(data.errors);
      }
      if (data.calendar) {
        setViewerBlocks(data.calendar.viewerBlocks || []);
      }
    }
    setSelectedStartDate(date);
    setIsLoading(false);
  };

  const onCloseDialog = () => {
    setSelectedDatetime(null);
  };

  const scrollToEventReservationView = (force?: boolean) => {
    if (!eventReservationRef.current) return;
    if (!isTablet && !force) return;
    const rect = eventReservationRef.current.getBoundingClientRect();
    const y = rect.top + window.pageYOffset - 80;
    window.scroll(0, y);
  };

  const onClickCalendarSetting = (e: SyntheticEvent) => {
    if (!props.viewer?.isTemporary) return;

    // 仮ユーザのみダイアログにする
    e.preventDefault();
    setIsShowingCalendarList(true);
  };

  const onCloseCalendarListDialog = async () => {
    setIsShowingCalendarList(false);

    if (
      calendarListDialog.state.hasChanged &&
      props.viewer &&
      !isOwnEventType
    ) {
      if (isWeeklyView) {
        setIsLoading(true);
        const periodStart = getDateTime().startOf('day');
        const periodEnd = periodStart.plus({ day: 7 }).endOf('day');
        const data = await fetch(periodStart!, periodEnd, timeZone);
        setIsLoading(false);
        toWeekView(periodStart, calendarDateList, calendarAllTimeDateList);
        setSelectedStartDate(periodStart);
        setIsLoading(false);
      } else {
        const periodStart = getDateTime().startOf('month');
        const periodEnd = periodStart.endOf('month');

        const data = await fetch(periodStart, periodEnd, timeZone);
        const currentDate = periodStart;
        const { days, firstAvailableTimeList } = getAvailableDays(
          calendarDateList,
          currentDate,
          periodEnd,
          isHostDiagnosticMode,
        );
        setSelectedTimeList(firstAvailableTimeList);
        setAvailableDays(days);
        setSelectedStartDate(periodStart);
      }
    }
  };

  const onCloseServiceRestrictedDialog = () => {
    setIsExceedServiceRestrict(false);
  };

  const onSubmitServices = () => {
    setActiveStep(Steps.Contact);
    scrollToEventReservationView();
  };

  const onSelectService = (selectService: EventTypeServiceListItem) => {
    const selectedIndex = selectedServices.findIndex((service) => {
      return service.uid === selectService.uid;
    });

    let selectedServicesCopy = [...selectedServices];
    if (props.eventType.setting.isMultiselectService) {
      if (selectedIndex === -1) {
        selectedServicesCopy.push(selectService);
      } else {
        selectedServicesCopy.splice(selectedIndex, 1);
      }
      setSelectedServices(selectedServicesCopy);
    } else {
      if (selectedIndex === -1) {
        selectedServicesCopy = selectedServicesCopy.filter(
          (service) => service.isRequired,
        );
        selectedServicesCopy.push(selectService);
      } else {
        selectedServicesCopy.splice(selectedIndex, 1);
      }
      setSelectedServices(selectedServicesCopy);
    }

    setIsPaidServiceSelected(
      selectedServicesCopy.some((s) => Number(s.isPaid) === 1),
    );
  };

  const onClickNextParticipantsListDialog = () => {
    setIsShowingParticipants(false);
    setShowingParticipants([]);
    // ロケーション有無で遷移先を変更
    setActiveStep(
      props.locationIds.length > 0 ? Steps.Location : Steps.Contact,
    );
    scrollToEventReservationView(isWeeklyView);
  };

  const onClickInquiry = () => {
    setIsShowingInquityForm(true);
  };

  const onCloseInquiry = () => {
    setIsShowingInquityForm(false);
  };

  const onSubmitInquiry = async () => {
    if (!validateInquiryForm()) {
      setIsLoading(true);
      // 少しローディングさせてから戻す
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
      return;
    }
    if (!name || !email || !message) return;

    const data: InquieryCreateBody = {
      name: name,
      email: email,
      message: message,
      company,
      projectId: props.eventType.projectId,
    };
    setIsLoading(true);
    const result = await post(
      apiPath(`/v1/event_types/${props.eventType.id}/inquiry`),
      { data },
    );
    if (!result.isSuccess) {
      setErrors(result.errors);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    return result;
  };

  const onCloseParticipantsListDialog = () => {
    setIsShowingParticipants(false);
    setShowingParticipants([]);
  };

  const onCloseSnackbar = () => {
    setSnackbarMessage(null);
  };

  const onChangeIsShowViewerCalendar = (isOn: boolean) => {
    setIsShowViewerCalendar(isOn);
    setPersistentItem(cacheKey, isOn, infiniteCacheExpiration);
  };

  const onClickHostDianosticMode = () => {
    const toMode = !isHostDiagnosticMode;
    setIsHostDiagnosticMode(toMode);
    if (toMode === true) {
      if (viewType === CalendarViewType.WeekCalendar) {
        const debugViewType = CalendarViewType.Week;
        setViewType(debugViewType);
      }
    } else {
      if (defaultViewType === CalendarViewType.WeekCalendar) {
        setViewType(CalendarViewType.WeekCalendar);
      }
    }
  };

  const onClickBlockTime = (time: string, reasons: any[]) => {
    setBlockTimeReasons(reasons);
  };

  const onCloseBlockReason = (e: any) => {
    setBlockTimeReasons([]);
  };

  const onClickViewMoreListViewType = async () => {
    if (isListView && !isLoading) {
      setIsLoadingViewMore(true);
      const start = listViewCursorDate?.plus({
        days: 1,
      });
      const end = start?.plus({ days: listViewDurationDays });
      if (start && end) {
        const data = await fetch(start, end, timeZone);
        const { days, firstAvailableTimeList } = getAvailableDays(
          calendarDateList,
          start,
          end,
          isHostDiagnosticMode,
        );
        // リスト表示は見える日付が増え続けるので追加していく
        setSelectedTimeList([...selectedTimeList, ...firstAvailableTimeList]);
        setAvailableDays([...availableDays, ...days]);
        setListViewCursorDate(end.set({ hour: 0, minute: 0, second: 0 }));
      }
      setIsLoadingViewMore(false);
    }
  };

  // カレンダー連携エラーがあるかどうかを反映
  const hasCalendarIntegrationError = useMemo(() => {
    return (
      props.viewer?.uid === props.host.uid &&
      typeof calendarIntegrationError === 'object' &&
      Object.keys(calendarIntegrationError).length > 0 &&
      Object.values(calendarIntegrationError)
        .flatMap((e) => e)
        .some(
          (e: any) =>
            typeof e === 'object' &&
            (e.code === 'WARN-204002' || e.code === 'WARN-204003'),
        )
    );
  }, [props.viewer, props.host, calendarIntegrationError]);

  let customLocation = null;
  let address = null;
  let access = null;
  if (props.eventType.location) {
    customLocation = props.eventType.location.customLocation;
    address = props.eventType.location.address;
    access = props.eventType.location.access;
  }

  const hasTeamLogo = props.team.profileImage !== null;
  const hasTeamHeaderLogo = props.team.headerTeamImage !== null;
  const requireRightMargin =
    !hasTeamLogo && viewType === CalendarViewType.Month;

  const isLightColor = useMemo(
    () =>
      !eventTypeThemeColors.includes(props.eventType.color) &&
      relativeLuminanceW3C(props.eventType.color) >= 0.5,
    [props.eventType],
  );

  const hostImage =
    props.eventType.hostOperationType === HostOperationType.Team
      ? props.team.profileImage
      : props.host.profileImage;
  const teamImage =
    props.eventType.hostOperationType === HostOperationType.None
      ? props.team.profileImage
      : null;
  const planId = props.eventType.planId;
  let descriptionKey = `description ${activeStep}`;
  if (
    activeStep === Steps.PaymentPreview &&
    selectedPaymentMethod === PaymentMethod.Card
  ) {
    descriptionKey += ` card`;
  } else if (activeStep === Steps.Contact && isProvidedContact) {
    descriptionKey += ` providedContact`;
  }

  const onChangeViewType = (viewType: CalendarViewType) => {
    setViewType(viewType);
  };

  return {
    state: {
      activeStep,
      timeZone,
      timeZones,
      isOpenReservation,
      isDoubleBooked,
      exceededInfo,
      isConfirmLoading,
      selectedDate,
      selectedDatetime,
      errors,
      isDisabled,
      isLoading,
      isLoadingViewMore,
      snackbarMessage,
      selectedLocationId,
      email,
      name,
      company,
      ccEmails,
      phoneNumber,
      textRemindCountry,
      textRemindNumber,
      message,
      selectedTimeList,
      selectedTimeLists,
      allTimeLists,
      defaultDate,
      availableDays,
      isInitLoaded,
      initialStep,
      isShowLocation,
      isFetchFailed,
      isWeeklyView,
      selectedStartDate,
      isWeeklyDataInitialized,
      eventReservationRef,
      hasCalendarIntegrationError,
      calendarIntegrationError,
      customQuestions,
      customQuestionAnswers,
      isShowingCalendarList,
      calendarListDialog: calendarListDialog.state,
      viewerBlocks,
      viewerTeamUser,
      selectedServices,
      isPaidServiceSelected,
      isShowingParticipants,
      showingParticipants,
      selectedPaymentMethod,
      isShowingInquityForm,
      customOtherAnswers,
      isOwnEventType,
      isHostDiagnosticMode,
      isHost,
      blockTimeReasons,
      startWeekDay,
      isFetching,
      isProvidedContact,
      hostImage,
      teamImage,
      requireRightMargin,
      descriptionKey,
      accountConnectMode,
      hasTeamHeaderLogo,
      planId,
      isLightColor,
      customLocation,
      address,
      access,
      displayWeekdays,
      calendarDateList,
      isSelectedServicesHidePriceOnly,
      restrictServiceUsages,
      isExceedServiceRestrict,
      viewType,
      isShowViewerCalendar,
      isAdmin,
    },
    actions: {
      onSubmit,
      onSubmitContact,
      onSubmitServices,
      onSubmitPayment,
      onSelectService,
      onSelectPaymentMethod,
      onClickBack,
      reloadScreen,
      onUpdate,
      onCloseDialog,
      onDeleteTag,
      onSubmitTag,
      onBlur,
      onInputChangeTag,
      onChange,
      onChangeTextRemindNumber,
      onTimeSelect,
      onTimeZoneSelect,
      onDateSelect,
      onChangeMonth,
      onSelectLocationType,
      onClickCalendarSetting,
      onCloseCalendarListDialog,
      onClickNextParticipantsListDialog,
      onCloseParticipantsListDialog,
      onClickTerm,
      onClickInquiry,
      onCloseInquiry,
      onSubmitInquiry,
      onClickOtherAnswer,
      onCloseSnackbar,
      onClickHostDianosticMode,
      onClickBlockTime,
      onCloseBlockReason,
      onClickViewMoreListViewType,
      onCloseServiceRestrictedDialog,
      calendarListDialog: calendarListDialog.actions,
      onChangeViewType,
      updateTimeZone,
      onChangeIsShowViewerCalendar,
    },
  };
}
